import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { atom, useAtom } from 'jotai'
import { FEATURE_FLAGS } from '@highlight/analytics'
import { useFlags } from '@highlight/analytics/react'
import { useGlobalEvent } from '@highlight/ipc/react'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'
import { usePersonalization } from '@renderer/hooks/usePersonalization'
import { useUser } from '@renderer/hooks/useUser'
import type { AudioNotePhase } from '../audio-note/use-audio-note-phase'
import { useIsMiniMenuOpen, useMiniNotched } from '../atoms'
import { trackNotchAction } from '../track-notch'
import {
  deviceOnlyMemory,
  dueMoment,
  isEligibleForSetupCall,
  mergeMemory,
  snoozeUntil,
  type SetupCallMemory,
  type SetupCallMoment,
} from './schedule'
import {
  useSetupCallState,
  useUpdateSetupCallInvite,
  type SetupCallInvite,
  type SetupCallInviteChange,
} from './setup-call-api'

/** What the notch holds under its ears: the invite, the picker, the time zones, or the booked confirmation. */
export type SetupCallView = 'invite' | 'book' | 'zone' | 'booked'

export interface SetupCallOpen {
  view: SetupCallView
  /** Opened by the product rather than the user, so it settles back to the ears on its own. */
  isPeek: boolean
  /** Settings > Developers stands in for the schedule: the first showing, or the return after Maybe later. */
  moment?: SetupCallMoment
}

/** Help > Book a setup session, from any window. */
export const OPEN_SETUP_CALL_EVENT = 'setup-call:open'

/** What `OPEN_SETUP_CALL_EVENT` may carry; Settings > Developers forces a view past the flag. */
export type OpenSetupCallRequest = { view?: SetupCallView; force?: boolean; moment?: SetupCallMoment }

const OPEN_DELAY_MS = 1_000
const CLOCK_MS = 60_000

/** Where the invite's answers lived before the account kept them; still read, never written. */
const deviceMemoryAtom = atomWithGlobalStore<SetupCallMemory & { userId: string }>('user', 'setupCall.invite', {
  userId: '',
})
const openAtom = atom<SetupCallOpen | null>(null)

function toMemory(invite: SetupCallInvite): SetupCallMemory {
  return {
    firstActionAt: invite.firstActionAt ? Date.parse(invite.firstActionAt) : undefined,
    snoozedUntil: invite.snoozedUntil ? Date.parse(invite.snoozedUntil) : undefined,
    dismissed: invite.dismissed,
    peeked: invite.peeked ?? undefined,
  }
}

/** Only the fields given, so an answer never blanks the ones it doesn't mention. */
function toChange(memory: Partial<SetupCallMemory>): SetupCallInviteChange {
  return {
    ...(memory.firstActionAt && { firstActionAt: new Date(memory.firstActionAt).toISOString() }),
    ...(memory.snoozedUntil && { snoozedUntil: new Date(memory.snoozedUntil).toISOString() }),
    ...(memory.dismissed && { dismissed: true as const }),
    ...(memory.peeked && { peeked: memory.peeked }),
  }
}

/** The account's answers to the invite merged with this device's older ones, which are sent up whenever the account lacks them. */
function useSetupCallMemory(userId: string | undefined, invite: SetupCallInvite | undefined) {
  const [stored] = useAtom(deviceMemoryAtom)
  const { mutate } = useUpdateSetupCallInvite()
  // The device store isn't keyed by account, so another account's answers read as none.
  const device = stored.userId === userId ? stored : undefined
  const account = useMemo(() => invite && toMemory(invite), [invite])

  // Keyed on what's sent rather than latched, so answers the device store loads late still go up, each set once
  const lastSent = useRef<string>()
  useEffect(() => {
    if (!account) return
    const change = toChange(deviceOnlyMemory(account, device))
    const key = JSON.stringify(change)
    if (key === '{}' || key === lastSent.current) return
    lastSent.current = key
    mutate(change)
  }, [account, device, mutate])

  const memory = useMemo(() => account && mergeMemory(account, device), [account, device])
  const update = useCallback((change: Partial<SetupCallMemory>) => mutate(toChange(change)), [mutate])
  return [memory, update] as const
}

function useMinuteClock() {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), CLOCK_MS)
    return () => window.clearInterval(id)
  }, [])
  return now
}

/** Records the first audio note finished, action staged or run, or meeting summary closed. */
function useFirstMeaningfulAction(phase: AudioNotePhase, onAction: (() => void) | null) {
  const previousPhase = useRef(phase)
  useEffect(() => {
    // The finished notch is the meeting's summary; it going back to idle is the note finished and the summary closed.
    if (previousPhase.current === 'finished' && phase === 'idle') onAction?.()
    previousPhase.current = phase
  }, [phase, onAction])
  useGlobalEvent('staged-action:new', () => onAction?.())
  useGlobalEvent('staged-action:invoked', () => onAction?.())
}

export interface SetupCallOffer {
  /** What the ears say; null when they're not up. */
  moment: SetupCallMoment | null
  open: SetupCallOpen | null
  show: (view: SetupCallView) => void
  /** Back to the ears, the offer still standing. */
  settle: () => void
  maybeLater: () => void
  neverShow: () => void
}

/** The notch's setup-call invite: when it's due, what's open under the ears, and how the user lets it go. */
export function useSetupCallOffer(phase: AudioNotePhase): SetupCallOffer {
  const flags = useFlags({ live: true })
  const isEnabled = flags.isEnabled(FEATURE_FLAGS.SETUP_CALL_INVITE, false)
  const requireProfessionalEmail = flags.isEnabled(FEATURE_FLAGS.SETUP_CALL_PROFESSIONAL_EMAIL_ONLY, false)
  const user = useUser()
  const { personalizationData } = usePersonalization()
  const state = useSetupCallState(isEnabled)
  const [memory, updateMemory] = useSetupCallMemory(user?.id, state.data?.invite)
  const [open, setOpen] = useAtom(openAtom)
  const now = useMinuteClock()
  const isSurfaceOpen = useIsMiniMenuOpen()
  const [isNotched] = useMiniNotched()

  const isEligible =
    isEnabled &&
    state.data?.booking === null &&
    isEligibleForSetupCall(
      { occupation: personalizationData?.occupation, email: user?.email },
      { requireProfessionalEmail },
    )
  const signedUpAt = state.data?.signedUpAt
  // A gateway that predates invite answers couldn't remember a showing, so it gets none
  const due = isEligible && memory ? dueMoment(memory, signedUpAt ? Date.parse(signedUpAt) : undefined, now) : null

  const recordAction = useCallback(() => updateMemory({ firstActionAt: Date.now() }), [updateMemory])
  // Recorded before eligibility loads, so an action taken while the fetches are in flight still counts
  useFirstMeaningfulAction(phase, memory?.firstActionAt ? null : recordAction)

  const isIdle = phase === 'idle'
  const hasPeeked = memory?.peeked === due

  useEffect(() => {
    // Never over Ask or the menu, and only in the notch: the pill can't show the invite
    if (!due || hasPeeked || !isIdle || !isNotched || isSurfaceOpen) return
    const id = window.setTimeout(() => {
      updateMemory({ peeked: due })
      setOpen((current) => current ?? { view: 'invite', isPeek: true })
      trackNotchAction('setup call', { step: 'invite shown', moment: due })
    }, OPEN_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [due, hasPeeked, isIdle, isNotched, isSurfaceOpen, setOpen, updateMemory])

  // A meeting outranks the offer: whatever was open goes, and the ears come back when it ends.
  useEffect(() => {
    if (isIdle || !open) return
    trackNotchAction('setup call', { step: 'closed for meeting', view: open.view })
    setOpen(null)
  }, [isIdle, open, setOpen])

  useGlobalEvent(OPEN_SETUP_CALL_EVENT, (request?: OpenSetupCallRequest) => {
    if (!isEnabled && !request?.force) return
    setOpen({ view: request?.view ?? 'book', isPeek: false, moment: request?.moment })
    if (!request?.force) trackNotchAction('setup call', { step: 'help opened' })
  })

  const show = useCallback(
    (view: SetupCallView) => setOpen((current) => ({ view, isPeek: false, moment: current?.moment })),
    [setOpen],
  )
  const settle = useCallback(() => setOpen(null), [setOpen])
  const maybeLater = useCallback(() => {
    updateMemory({ snoozedUntil: snoozeUntil(new Date()) })
    setOpen(null)
    trackNotchAction('setup call', { step: 'maybe later' })
  }, [setOpen, updateMemory])
  const neverShow = useCallback(() => {
    updateMemory({ dismissed: true })
    setOpen(null)
    trackNotchAction('setup call', { step: 'never show' })
  }, [setOpen, updateMemory])

  // The ears stand only while something hangs under them: resting, they widen the notch over menu-bar icons.
  const moment = isIdle && open ? (open.moment ?? due ?? 'first') : null

  const shown = isIdle ? open : null
  return useMemo(
    () => ({ moment, open: shown, show, settle, maybeLater, neverShow }),
    [moment, shown, show, settle, maybeLater, neverShow],
  )
}
